<template>
    <Modal size="m">
        <div class="coupon">
            <div class="coupon-top">
                <Element font="font-heading-5" class="coupon-top-amount">
                    {{ discountText }}
                </Element>
                <Badge
                    :text="coupon.code"
                    clas="coupon-top-code"
                    size="m"
                    color="semboPurple" />
            </div>

            <ButtonSecondary
                v-if="showCopyButton"
                :onClick="onButtonClick"
                icon="Copy"
                size="s"
                iconPosition="left">
                {{ t({ key: 'shared.copy_code' }) }}
            </ButtonSecondary>

            <FlightCoupon v-if="coupon.type === CouponType.Flight" :coupon="coupon" />
        </div>
    </Modal>
</template>

<script lang="ts">
    import { defineComponent, onMounted, onUnmounted, PropType } from 'vue';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { useClipboard } from '@vueuse/core';
    import { t } from '@shared/helpers/translation';
    import { configBase } from '@/config/base';
    import {
        logStatsigBookingCouponClose,
        logStatsigBookingCouponCodeCopied,
        logStatsigBookingCouponOpen,
    } from '@/tracking/statsig/booking.functions';
    import {
        Coupon,
        CouponPlacement,
        CouponType,
    } from '@/api/MyBooking/myBooking.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import Modal from '@ui/components/Modal/Modal.vue';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import Badge from '@ui/components/Badge/Badge.vue';
    import Element from '@ui/components/Element/Element.vue';
    import FlightCoupon from './Content/FlightCoupon.vue';

    export default defineComponent({
        name: 'CouponModal',
        components: { Element, Badge, Modal, ButtonSecondary, FlightCoupon },
        methods: { t },
        props: {
            coupon: {
                type: Object as PropType<Coupon>,
                required: true,
            },
            placement: {
                type: String as PropType<CouponPlacement>,
                required: true,
            },
        },
        setup(props) {
            const { coupon } = props;
            const { copy, isSupported } = useClipboard();

            const discountAmount = getMoneyObjectAsString(coupon.amount);
            const discountText = `${discountAmount} ${t({
                key: 'service_package.usp_hotel_voucher_title',
                format: LabelFormat.LowerCase,
            })}`;

            onMounted(() =>
                logStatsigBookingCouponOpen(coupon.type, props.placement, isSupported.value)
            );

            onUnmounted(() => logStatsigBookingCouponClose());

            const onButtonClick = () => {
                copy(props.coupon.code);
                logStatsigBookingCouponCodeCopied(props.coupon.type, props.placement);
            };

            // unfortunately it seems like Clipboard API isn't working on http,
            // so we will just show the button in Coolify
            const showCopyButton = isSupported.value || configBase.devMode;

            return {
                showCopyButton,
                discountText,
                onButtonClick,
                CouponType,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Coupon';
</style>
